import { motion } from 'framer-motion';
import styled from 'styled-components';
import { Controller, useForm } from 'react-hook-form';
import DatePicker from 'react-datepicker';
import { ko } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import { useEffect, useState } from 'react';
import Select from 'react-select';
import { Calendar } from '../../../icon/icon';
import { RadioButtonInput, RadioInput } from '../../../items/radio.input';
import { useHistory } from 'react-router-dom';
import { getCorporationList } from '../../../../api/report';
import { zodResolver } from '@hookform/resolvers/zod';
import { transactionStateSchema } from '../../../../schema/reportSchema';

const radioItems = [
  { key: 'thisMonth', value: '이번달' },
  { key: 'aMonth', value: '1개월' },
  { key: 'rangeSetting', value: '기간설정' },
];

const radioBtnItems = [
  { key: 'belonged', value: '일대차' },
  { key: 'rent', value: '마당차' },
];

interface CorporationListDto {
  corporationId: number;
  corporationName: string;
}

interface SearchConditionProps {
  handleConfirmClose: () => void;
  confirmText?: string;
  onClick?: () => void;
}

export const DairyStatementModal = ({
  handleConfirmClose,
  onClick,
  confirmText,
}: SearchConditionProps) => {
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(transactionStateSchema),
    defaultValues: {
      monthValue: 'thisMonth',
      startDate: firstDayOfMonth,
      endDate: lastDayOfMonth,
      reportType: 'belonged',
      name: '',
    },
  });

  const [disabled, setDisabled] = useState(true);
  const history = useHistory();

  const [corporationList, setCorporationList] = useState<CorporationListDto[]>(
    [],
  );

  const onSubmit = (data: any) => {
    console.log(data);
    const { startDate, endDate, reportType, name } = data;
    const selectedType = reportType;
    const borderLine = true;
    history.push({
      pathname: '/userDiary/transactionSatementRegist',
      state: {
        startDate,
        endDate,
        selectedType,
        borderLine,
        name,
      },
    });
    console.log(data);
  };

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value === 'rangeSetting') {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
  };

  useEffect(() => {
    const getCorporationListData = async () => {
      try {
        const response = await getCorporationList();
        setCorporationList(response);
      } catch (error) {
        console.error(error);
      }
    };

    getCorporationListData();
  }, []);

  return (
    <Container>
      <motion.div
        className="wrapper"
        initial={{ y: '100%' }}
        animate={{ y: '0%' }}
        exit={{ y: '100%' }}
        transition={{
          type: 'spring',
          stiffness: 400,
          damping: 40,
        }}
      >
        <div className="overlay" onClick={handleConfirmClose}></div>
        <div className="wrap-shape">
          <div className="header">
            <span>거래 명세표 생성</span>
          </div>
          <div className="body">
            <div>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="range-date">
                  <RadioInput
                    register={register}
                    items={radioItems}
                    onChange={handleRadioChange}
                  />
                </div>
                <DatePickerContainer>
                  <div className="inner">
                    <Controller
                      name="startDate"
                      control={control}
                      render={({ field }) => (
                        <CustomDatePicker
                          className="search-input"
                          locale={ko}
                          dateFormat="yyyy-MM-dd"
                          selected={field.value}
                          onChange={(date: any) => field.onChange(date)} // form의 상태로 날짜 업데이트
                          disabled={disabled}
                          placeholderText="날짜 선택"
                          withPortal
                        />
                      )}
                    />
                    <div className="icon-box">
                      <Calendar />
                    </div>
                  </div>
                  <span>~</span>
                  <div className="inner">
                    <Controller
                      name="endDate"
                      control={control}
                      render={({ field }) => (
                        <CustomDatePicker
                          className="search-input"
                          locale={ko}
                          dateFormat="yyyy-MM-dd"
                          selected={field.value}
                          onChange={(date: any) => field.onChange(date)} // form의 상태로 날짜 업데이트
                          disabled={disabled}
                          placeholderText="날짜 선택"
                          withPortal
                        />
                      )}
                    />
                    <div className="icon-box">
                      <Calendar />
                    </div>
                  </div>
                </DatePickerContainer>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <Select
                      className="search-select"
                      classNamePrefix="react-select"
                      options={corporationList.map((corp) => ({
                        value: corp.corporationName, // corporationName을 value로 설정
                        label: corp.corporationName, // corporationName을 label로 설정
                      }))}
                      onChange={(selectedOption) =>
                        field.onChange(
                          selectedOption ? selectedOption.value : null,
                        )
                      }
                      value={corporationList
                        .map((corp) => ({
                          value: corp.corporationName,
                          label: corp.corporationName,
                        }))
                        .find((option) => option.value === field.value)} // corporationName을 비교하여 현재 선택된 값을 설정
                      components={{ IndicatorSeparator: () => null }}
                      placeholder="회사 선택"
                      menuPlacement="top"
                      maxMenuHeight={100}
                    />
                  )}
                />
                {errors.name && <ValTxt>{String(errors.name.message)}</ValTxt>}
                <RadioButtonInput
                  items={radioBtnItems}
                  register={register}
                  registerName="reportType"
                />
                <div className="btn-box">
                  <button
                    type="button"
                    onClick={handleConfirmClose}
                    className="first-btn"
                  >
                    취소
                  </button>
                  <button className="second-btn">선택</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </motion.div>
    </Container>
  );
};

// style
const Container = styled.div`
  display: flex;
  align-items: flex-end;
  height: 100%;
  position: relative;
  .wrapper {
    width: 100%;
    height: 100%;
    .wrap-shape {
      background: ${(props) => props.theme.textWhiteColor};
      border-radius: 2rem 2rem 0 0;
      overflow: hidden;
      padding: 0.8rem 2.2rem;
      height: 35.3rem;
      width: 100%;
      position: absolute;
      bottom: 0;
      .header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        height: 4.4rem;
        span {
          font-size: 1.7rem;
          font-weight: 600;
        }
        .icon-box {
          display: flex;
          transform: translateX(1.6rem);
        }
      }
      .body {
        form {
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 1.2rem;
          margin-top: 0.9rem;
          input[type='text'] {
            padding: 1.4rem 1.8rem 1.5rem;
            border-radius: 0.8rem;
            border: 1px solid ${(props) => props.theme.searchBoxBorderedColor};
            &::placeholder {
              color: ${(props) => props.theme.searchPlaceholderColor};
            }
          }
        }
        .search-select {
          font-size: 1.4rem;
          .react-select__placeholder {
            color: ${(props) => props.theme.searchPlaceholderColor};
          }
          .react-select__control {
            border-color: ${(props) => props.theme.searchBoxBorderedColor};
            border-radius: 0.8rem;
            .react-select__value-container {
              padding: 1.1rem 1.6rem 0.9rem;
            }
          }
        }
        .btn-box {
          margin-top: 0.6rem;
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.8rem;
          button {
            width: 100%;
            background-color: ${(props) => props.theme.btnBlueColor};
            color: ${(props) => props.theme.textWhiteColor};
            border-radius: 0.8rem;
            border: 0;
            outline: none;
            font-size: 1.8rem;
            font-weight: 500;
            padding: 1.3rem 1rem;
            height: 4.6rem;
          }
          .first-btn {
            background-color: ${(props) => props.theme.logoutBtnBgColor};
            color: ${(props) => props.theme.gray900};
          }
          .second-btn {
            background-color: ${(props) => props.theme.btnBlueColor};
            color: ${(props) => props.theme.textWhiteColor};
          }
        }
      }
    }
  }
`;

const CustomDatePicker = styled(DatePicker)``;

export const DatePickerContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  span {
    margin: 0 1rem;
    font-size: 1.4rem;
    font-weight: 400;
    transform: translateY(1px);
    color: ${(props) => props.theme.joinLabelColor};
  }
  .inner {
    position: relative;
    .search-input {
      padding: 1.4rem 5rem 1.5rem 1.75rem;
      max-width: 15rem;
    }
    .react-datepicker {
      font-size: 1.4rem;
      font-weight: 400;
      .react-datepicker__header {
        background-color: ${(props) => props.theme.textWhiteColor};
      }
      .react-datepicker__current-month,
      .react-datepicker-time__header,
      .react-datepicker-year-header {
        font-size: 1.4rem;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .react-datepicker__day-name,
      .react-datepicker__day,
      .react-datepicker__time-name {
        width: 3rem;
        line-height: 3rem;
      }
      .react-datepicker__day--selected,
      .react-datepicker__day--in-selecting-range,
      .react-datepicker__day--in-range,
      .react-datepicker__month-text--selected,
      .react-datepicker__month-text--in-selecting-range,
      .react-datepicker__month-text--in-range,
      .react-datepicker__quarter-text--selected,
      .react-datepicker__quarter-text--in-selecting-range,
      .react-datepicker__quarter-text--in-range,
      .react-datepicker__year-text--selected,
      .react-datepicker__year-text--in-selecting-range,
      .react-datepicker__year-text--in-range {
        border-radius: 2rem;
        background-color: ${(props) => props.theme.btnBlueColor};
        color: ${(props) => props.theme.textWhiteColor};
      }
    }
    .react-datepicker-popper[data-placement='bottom']
      .react-datepicker__triangle {
      fill: ${(props) => props.theme.textWhiteColor};
      color: ${(props) => props.theme.textWhiteColor};
    }
    .icon-box {
      display: flex;
      position: absolute;
      top: 50%;
      right: 1.25rem;
      transform: translateY(-50%);
    }
  }
`;

const ValTxt = styled.span`
  color: ${(props) => props.theme.errorColor};
  font-size: 1.2rem;
  font-weight: 400;
  display: flex;
  margin: 0 1.7rem 0;
`;
